// Base component (not a leaf theme's override): a neutral component's JS must
// require the base component, since the carousel cannot assume any particular
// leaf theme sits in the chain.
const component = require('../component/component');

// Read-only carousel: a horizontal strip of windows (cards). Only a limited
// number are visible at once; previous/next side arrows and navigation dots
// move through the strip. Pure DOM/CSSOM behaviour with no CSS-framework
// dependency, so it lives at the base tier and every leaf theme inherits it
// unchanged. Visual state is exposed as data-* attributes the theme CSS reacts
// to (root[data-zit-overflow], dot[data-zit-dot-active]); the intended visible
// count is published as the --zit-carousel-visible custom property on the track
// for the theme's slide-width formula. The per-page count and scroll distances
// are MEASURED at runtime, so a responsive theme (fewer slides on narrow
// viewports) adapts without any extra config.
const carousel = Object.assign({}, component, {
    subelements: {
        track: null,
        previousButton: null,
        nextButton: null,
        dotsHolder: null,
        viewToggle: null,
    },
    showArrows: true,        // read from data-zit-show-arrows
    showDots: true,          // read from data-zit-show-dots
    availableViews: 'both',  // read from data-zit-available-views; 'slider', 'list' or 'both' (the only case with a toggle)
    view: 'slider',          // read from data-zit-view; 'slider' (scrolling strip) or 'list' (wrapped grid)
    visibleWindows: 3,       // read from data-zit-visible-windows
    scrollBy: 0,             // read from data-zit-scroll-by; 0 = scroll a whole page
    dotClass: '',            // read from data-zit-dot-class (theme's dot zone classes)
    allSlides: [],           // every window-card slide element, in document order
    slides: [],              // the currently-visible subset (recomputed by regenerate)

    init: function () {
        component.init.call(this);

        if (!this.subelements.track) {
            return;
        }

        // Collect every slide element (server-rendered, in document order). this.slides is
        // the currently-visible subset (recomputed by regenerate as cards are filtered in/
        // out); it starts as all of them.
        this.allSlides = Array.from(this.subelements.track.querySelectorAll('[data-zit-carousel-slide]'));
        this.slides = this.allSlides;

        // Publish the intended visible count so the theme's slide-width formula
        // (a calc() over --zit-carousel-visible) can size the slides. Set via
        // the CSSOM (not an inline style attribute), which is CSP-safe.
        this.subelements.track.style.setProperty('--zit-carousel-visible', this.visibleWindows);

        this.wire();
    },

    inited: function () {
        component.inited.call(this);

        // First layout pass once the DOM (and theme CSS) has settled.
        this.refresh();
    },

    wire: function () {
        // Arrow clicks scroll by the configured number of slides (scrollBy),
        // capped at a whole page (see effectiveSlides).
        if (this.subelements.previousButton) {
            this.subelements.previousButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.scrollSlides(-1);
            });
        }
        if (this.subelements.nextButton) {
            this.subelements.nextButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.scrollSlides(1);
            });
        }

        // View toggle (optional): each button carries data-zit-view-mode
        // ("slider"/"list"); one delegated listener switches the mode.
        if (this.subelements.viewToggle) {
            this.subelements.viewToggle.addEventListener('click', (e) => {
                let button = e.target.closest('[data-zit-view-mode]');
                if (!button) {
                    return;
                }
                e.preventDefault();
                this.setView(button.dataset.zitViewMode);
            });
        }

        // Keep the active dot in sync while the user scrolls (wheel/touch/drag),
        // debounced through the base timer helper.
        this.subelements.track.addEventListener('scroll', () => {
            this.startTimer('carouselScroll', () => { this.updateActiveDot(); }, 60);
        });

        // Recompute overflow + dots on viewport resize.
        window.addEventListener('resize', () => {
            this.startTimer('carouselResize', () => { this.refresh(); }, 120);
        });

        // Re-layout when a contained slide (window) is shown/hidden. The app-wide window
        // listener (app.js) owns slide visibility — it hides a slide once all its
        // components are hidden, and shows it when one reappears, dispatching bubbling
        // zitWindowShown / zitWindowHidden on the slide. The carousel only reflows: this
        // single delegated listener on the root catches the events from any slide and
        // recomputes the layout off the now-visible slides. Debounced because one filter
        // change can flip many slides in a burst.
        let reflow = () => { this.startTimer('carouselFilter', () => { this.regenerate(); }, 60); };
        this.element.addEventListener('zitWindowShown', reflow);
        this.element.addEventListener('zitWindowHidden', reflow);
    },

    // Width of one slide including the inter-slide gap, measured live so the
    // theme's CSS (fixed width or responsive) stays the single source of truth.
    slideStep: function () {
        if (!this.slides.length) {
            return this.subelements.track.clientWidth;
        }
        let gap = parseFloat(getComputedStyle(this.subelements.track).columnGap) || 0;
        return this.slides[0].offsetWidth + gap;
    },

    // How many whole slides currently fit in the track.
    visibleCount: function () {
        let step = this.slideStep();
        return step > 0 ? Math.max(1, Math.floor(this.subelements.track.clientWidth / step)) : 1;
    },

    hasOverflow: function () {
        // 4px tolerance to ignore sub-pixel rounding.
        return this.subelements.track.scrollWidth > this.subelements.track.clientWidth + 4;
    },

    // Re-derive the visible slide set and re-lay out: a slide hidden by the app-wide window
    // listener (app.js) drops out of this.slides, so slideStep/overflow/dots/paging operate
    // only on the slides that are actually visible.
    regenerate: function () {
        this.slides = this.allSlides.filter((slide) => !this.hm.helper.isHidden(slide));

        this.refresh();
    },

    // Switch between the scrolling slider and the wrapped list. Reflects the mode on
    // the root (data-zit-view, the CSS hook), flips the active toggle button, resets
    // the scroll position (a list has none), and re-lays out — refresh() then drops
    // the slider chrome (arrows/dots) in list mode.
    setView: function (mode) {
        // A single-view carousel renders no toggle, so nothing normally calls this —
        // but a programmatic call can never take it out of the one mode it offers.
        if (this.availableViews !== 'both') {
            mode = this.availableViews;
        }

        this.view = (mode === 'list') ? 'list' : 'slider';
        this.element.dataset.zitView = this.view;

        if (this.subelements.viewToggle) {
            this.subelements.viewToggle.querySelectorAll('[data-zit-view-mode]').forEach((button) => {
                button.dataset.zitViewActive = (button.dataset.zitViewMode === this.view) ? 'true' : 'false';
            });
        }

        this.subelements.track.scrollLeft = 0;
        this.refresh();
    },

    refresh: function () {
        // "More than one page of slides" — there is something to scroll to. A single page
        // (everything currently visible fits) means no arrows and no dots. The list view
        // wraps its cards (no horizontal paging), so it never shows the slider chrome.
        let overflow = (this.view !== 'list') && this.hasOverflow();

        // Root flag the theme CSS still reacts to (e.g. centring a short, non-scrolling row).
        this.element.dataset.zitOverflow = overflow ? 'true' : 'false';

        // Hide the prev/next arrows + the dots holder on a single page, show them again on
        // overflow — driven here in JS (via the theme-mapped displayNone class) so the base
        // component behaves the same regardless of whether a theme wrote the data-zit-overflow
        // CSS. refresh() runs on init, on resize, and on filter-driven slide show/hide (see
        // wire()), so the controls reappear the moment a wider viewport or a relaxed filter
        // brings back enough slides to need paging.
        if (this.subelements.previousButton) {
            this.hm.helper.showElement(this.subelements.previousButton, overflow);
        }
        if (this.subelements.nextButton) {
            this.hm.helper.showElement(this.subelements.nextButton, overflow);
        }
        if (this.subelements.dotsHolder) {
            this.hm.helper.showElement(this.subelements.dotsHolder, overflow && this.showDots);
        }

        if (overflow && this.showDots) {
            this.buildDots();
        } else if (this.subelements.dotsHolder) {
            this.subelements.dotsHolder.replaceChildren();
        }
    },

    buildDots: function () {
        if (!this.subelements.dotsHolder) {
            return;
        }
        this.subelements.dotsHolder.replaceChildren();

        let visible = this.visibleCount();
        let pages = Math.ceil(this.slides.length / visible);

        for (let i = 0; i < pages; i++) {
            let dot = document.createElement('button');
            dot.type = 'button';
            // Static dot styling comes from the theme's dot zone; the active
            // state is a data attribute the theme CSS reacts to.
            if (this.dotClass) {
                dot.className = this.dotClass;
            }
            dot.dataset.zitDotIndex = i;
            dot.addEventListener('click', () => { this.goToPage(i); });
            this.subelements.dotsHolder.appendChild(dot);
        }

        this.updateActiveDot();
    },

    // Which page (group of visibleCount slides) is currently scrolled into view.
    currentPage: function () {
        let pageWidth = this.slideStep() * this.visibleCount();
        return pageWidth > 0 ? Math.round(this.subelements.track.scrollLeft / pageWidth) : 0;
    },

    updateActiveDot: function () {
        if (!this.subelements.dotsHolder) {
            return;
        }
        let page = this.currentPage();
        let dots = this.subelements.dotsHolder.querySelectorAll('[data-zit-dot-index]');
        dots.forEach((dot, i) => {
            dot.dataset.zitDotActive = (i === page) ? 'true' : 'false';
        });
    },

    // How many slides one arrow click moves: the configured scrollBy, capped at
    // a whole page. scrollBy <= 0 (the default) means "scroll a whole page", and
    // any value >= the slides-per-page also resolves to a page.
    effectiveSlides: function () {
        let visible = this.visibleCount();
        if (this.scrollBy <= 0) {
            return visible;
        }
        return Math.min(this.scrollBy, visible);
    },

    scrollSlides: function (direction) {
        let distance = direction * this.slideStep() * this.effectiveSlides();
        this.subelements.track.scrollBy({ left: distance, behavior: 'smooth' });
    },

    goToPage: function (page) {
        let visible = this.visibleCount();
        let index = Math.min(page * visible, this.slides.length - 1);
        if (index < 0) {
            index = 0;
        }
        let target = this.slides[index];
        if (target) {
            // Align the page's first slide with the track's left edge.
            this.subelements.track.scrollTo({
                left: target.offsetLeft - this.subelements.track.offsetLeft,
                behavior: 'smooth'
            });
        }
    },
});

module.exports = carousel;
